import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, KeyRound, User, ArrowRight, Loader2, Sun, Moon } from 'lucide-react';
import useAuthStore from '../stores/useAuthStore';
import { useTheme } from '../contexts/ThemeContext';

export default function Login() {
    const { login, sessaoExpirada, limparSessaoExpirada } = useAuthStore();
    const { theme, toggleTheme } = useTheme();
    const [usuario, setUsuario] = useState('');
    const [senha, setSenha] = useState('');
    const [mostrarSenha, setMostrarSenha] = useState(false);
    const [erro, setErro] = useState(sessaoExpirada ? 'Sua sessão expirou. Faça login novamente.' : '');
    const [carregando, setCarregando] = useState(false);
    const [modo, setModo] = useState('login');
    const [msgEsqueci, setMsgEsqueci] = useState('');

    // Mostra o aviso só uma vez — evita repetir depois que o usuário já tentar logar de novo.
    useEffect(() => {
        if (sessaoExpirada) limparSessaoExpirada();
    }, []);

    // Máquina não é mais escolhida aqui (04/08/2026, decisão do usuário) — vem
    // do(s) centro(s) de trabalho liberado(s) pro usuário (Gestão de Usuários).
    // Se houver mais de uma máquina candidata, a tela de Produção pergunta.
    const handleLogin = async (e) => {
        e.preventDefault();
        setErro('');
        setCarregando(true);
        const resultado = await login(usuario, senha);
        setCarregando(false);
        if (!resultado.ok) setErro(resultado.erro);
    };

    const handleEsqueci = (e) => {
        e.preventDefault();
        if (!usuario.trim()) {
            setErro('Informe sua matrícula para redefinir a senha.');
            return;
        }
        setMsgEsqueci('Solicitação enviada! O administrador irá redefinir sua senha.');
        setErro('');
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
            {/* Theme Toggle */}
            <button
                onClick={toggleTheme}
                className="fixed top-5 right-5 z-50 p-2.5 rounded-2xl bg-white/[0.06] border border-white/[0.08] text-anthracite-400 hover:text-anthracite-200 hover:bg-white/[0.1] transition-all duration-200 shadow-glass"
                title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-[0.015]" style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: '80px 80px',
                }} />
            </div>

            <div className="w-full max-w-[420px] relative z-10 animate-fade-in">
                {/* Logo */}
                <div className="text-center mb-10">
                    <img
                        src={theme === 'dark' ? `${import.meta.env.BASE_URL}logo-tecnofibras-negativo.png` : `${import.meta.env.BASE_URL}logo-tecnofibras.png`}
                        alt="Tecnofibras"
                        className="max-w-[210px] w-full mx-auto object-contain"
                    />
                    <h1 className="text-3xl font-bold text-anthracite-50 tracking-tight mt-6">Chão de Fábrica</h1>
                    <p className="text-[11px] font-bold text-anthracite-500 tracking-[0.2em] uppercase mt-2.5">Tecnofibras · Compósitos SMC e RTM/AT</p>
                </div>

                {/* Card */}
                <div className="mes-card p-8">
                    {modo === 'login' ? (
                        <>
                            <div className="mb-7">
                                <h2 className="text-lg font-semibold text-anthracite-100">Entrar no sistema</h2>
                                <p className="text-[13px] text-anthracite-500 mt-1">Acesse o painel de operações industriais</p>
                            </div>

                            <form onSubmit={handleLogin} className="space-y-5">
                                <div>
                                    {/* Login por matrícula (29/08/2026, pedido do usuário) —
                                        o campo continua se chamando "usuario" no payload (não
                                        muda backend/JWT por causa disso), só o rótulo/placeholder
                                        visível mudou. */}
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2.5">Matrícula</label>
                                    <div className="relative">
                                        <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-anthracite-500" />
                                        <input
                                            id="login-usuario"
                                            type="text"
                                            autoComplete="username"
                                            value={usuario}
                                            onChange={e => setUsuario(e.target.value)}
                                            placeholder="Digite sua matrícula"
                                            className="mes-input pl-11"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2.5">Senha</label>
                                    <div className="relative">
                                        <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-anthracite-500" />
                                        <input
                                            id="login-senha"
                                            type={mostrarSenha ? 'text' : 'password'}
                                            autoComplete="current-password"
                                            value={senha}
                                            onChange={e => setSenha(e.target.value)}
                                            placeholder="Digite sua senha"
                                            className="mes-input pl-11 pr-11"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setMostrarSenha(!mostrarSenha)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-anthracite-500 hover:text-anthracite-300 transition-colors"
                                        >
                                            {mostrarSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {erro && (
                                    <div className="rounded-2xl px-4 py-3 text-[12px] font-medium bg-red-500/10 border border-red-500/15 text-red-400">
                                        {erro}
                                    </div>
                                )}

                                <button
                                    id="btn-entrar"
                                    type="submit"
                                    disabled={carregando}
                                    className="mes-btn-primary w-full py-3.5"
                                >
                                    {carregando ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Entrando...
                                        </>
                                    ) : (
                                        <>
                                            Entrar
                                            <ArrowRight size={16} />
                                        </>
                                    )}
                                </button>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => { setModo('esqueci'); setErro(''); }}
                                        className="text-[12px] text-anthracite-500 hover:text-anthracite-300 transition-colors font-medium"
                                    >
                                        Esqueci minha senha
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <>
                            <div className="mb-7">
                                <h2 className="text-lg font-semibold text-anthracite-100">Redefinir senha</h2>
                                <p className="text-[13px] text-anthracite-500 mt-1">
                                    Informe sua matrícula e envie a solicitação ao administrador.
                                </p>
                            </div>

                            <form onSubmit={handleEsqueci} className="space-y-5">
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2.5">Matrícula</label>
                                    <div className="relative">
                                        <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-anthracite-500" />
                                        <input
                                            id="esqueci-usuario"
                                            type="text"
                                            value={usuario}
                                            onChange={e => setUsuario(e.target.value)}
                                            placeholder="Digite sua matrícula"
                                            className="mes-input pl-11"
                                        />
                                    </div>
                                </div>

                                {erro && (
                                    <div className="rounded-2xl px-4 py-3 text-[12px] font-medium bg-red-500/10 border border-red-500/15 text-red-400">
                                        {erro}
                                    </div>
                                )}
                                {msgEsqueci && (
                                    <div className="rounded-2xl px-4 py-3 text-[12px] font-medium bg-petrol-500/10 border border-petrol-500/15 text-petrol-400">
                                        {msgEsqueci}
                                    </div>
                                )}

                                <button
                                    id="btn-solicitar-reset"
                                    type="submit"
                                    className="mes-btn-primary w-full py-3.5"
                                >
                                    Enviar solicitação
                                </button>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => { setModo('login'); setErro(''); setMsgEsqueci(''); }}
                                        className="text-[12px] text-anthracite-500 hover:text-anthracite-300 transition-colors font-medium"
                                    >
                                        Voltar ao login
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>

                <p className="text-center text-[11px] text-anthracite-600 mt-8">
                    Tecnofibras · Chão de Fábrica · Joinville/SC
                </p>
            </div>
        </div>
    );
}
